import React from 'react';
import PropTypes from 'prop-types';
import getInlineStyle from '../utils/style/getInlineStyle';
import getBorder from '../utils/style/getBorder';
import getBackground from '../utils/style/getBackground';
import getSpacing from '../utils/style/getSpacing';
import getText from '../utils/style/getText';
import getJSONValue from '../utils/fields/getJSONValue';
import { defaultMemoize } from 'reselect';
import memoizeOn from '../utils/memoizeOn';
import convertToPseudoClasses from '../utils/style/convertToPseudoClasses';

/**
 * A base class for components (such as widgets) that can use nucleus-site-editor styling
 */
export default class ThemeableComponent extends React.Component {
  static propTypes = {
    themeImages: PropTypes.object.isRequired,
    metaData: PropTypes.object,
    style: PropTypes.object,
    pseudoState: PropTypes.string,
    isSelectedItem: PropTypes.bool,
    currentElementKey: PropTypes.string
  };

  /**
   * The default getStyleObject function. This function should be called
   * by every widget wrapper to convert the passed in theme style object
   * into an inline style object that can be consumed by the corresponding
   * pure UI component. This can be overridden on a per widget basis if
   * that widget needs to more specifically style more of its sub-elements.
   */
  getStyleObject() {
    const { themeImages, style, metaData = {} } = this.props;
    return this._getStyleObject(style, themeImages, metaData.disableInput);
  }

  _getStyleObject = defaultMemoize((style, themeImages, disableInput) => {
    return {
      container: {
        ...getInlineStyle(
          style,
          style.customSettings,
          style.palette,
          style.fontPalette,
          themeImages,
          { useText: false }
        ),
        flex: '1 1 auto',
        pointerEvents: disableInput ? 'none' : undefined
      }
    };
  });

  /**
   * Gets the style for an element in a merged theme style object.
   * Either inherits from a mapped parent element or uses the custom
   * settings defined in the object.
   */
  getElementInlineStyle(key) {
    const { themeImages, style, pseudoState, isSelectedItem, currentElementKey } = this.props;
    return this._getElementInlineStyle(
      key,
      style,
      themeImages,
      pseudoState,
      isSelectedItem,
      currentElementKey
    );
  }

  _getElementInlineStyle = memoizeOn(
    key => key,
    (key, style, themeImages, pseudoState, isSelectedItem, currentElementKey) => {
      const styles = getJSONValue(style, key);

      if (!styles) {
        return {};
      }

      if (styles.styleMapping === 'custom') {
        return convertToPseudoClasses({
          styles: getInlineStyle(
            styles,
            styles.customSettings,
            style.palette,
            style.fontPalette,
            themeImages,
            { customFonts: style.customFonts }
          ),
          styleMapping: styles.styleMapping,
          isSelectedItem,
          pseudoState,
          key,
          currentElementKey
        });
      }
      return convertToPseudoClasses({
        styles: getInlineStyle(
          style.elements[styles.styleMapping],
          styles.customSettings,
          style.palette,
          style.fontPalette,
          themeImages,
          { customFonts: style.customFonts }
        ),
        styleMapping: styles.styleMapping,
        isSelectedItem,
        pseudoState,
        key,
        currentElementKey
      });
    }
  );

  /**
   * Gets the border style for an element in a merged theme style object.
   * Either inherits from a mapped parent element or uses the custom
   * settings defined in the object.
   */
  getElementBorder(key) {
    const { style, pseudoState, isSelectedItem, currentElementKey } = this.props;
    this._getElementBorder(key, style, pseudoState, isSelectedItem, currentElementKey);
  }

  _getElementBorder = memoizeOn(
    key => key,
    (key, style, pseudoState, isSelectedItem, currentElementKey) => {
      const styles = getJSONValue(style, key);
      if (!styles) {
        return {};
      }
      return styles.styleMapping === 'custom'
        ? convertToPseudoClasses({
          styles: getBorder(styles, styles.customSettings, style.palette),
          styleMapping: styles.styleMapping,
          isSelectedItem,
          pseudoState,
          key,
          currentElementKey
        })
        : convertToPseudoClasses({
          styles: getBorder(
            style.elements[styles.styleMapping],
            styles.customSettings,
            style.palette
          ),
          styleMapping: styles.styleMapping,
          isSelectedItem,
          pseudoState,
          key,
          currentElementKey
        });
    }
  );

  /**
   * Gets the background style for an element in a merged theme style object.
   * Either inherits from a mapped parent element or uses the custom
   * settings defined in the object.
   */
  getElementBackground(key) {
    const { themeImages, style, pseudoState, isSelectedItem, currentElementKey } = this.props;
    return this._getElementBackground(key, style, themeImages, pseudoState, isSelectedItem, currentElementKey);
  }

  _getElementBackground = memoizeOn(
    key => key,
    (key, style, themeImages, pseudoState, isSelectedItem, currentElementKey) => {
      const styles = getJSONValue(style, key);
      if (!styles) {
        return {};
      }
      return styles.styleMapping === 'custom'
        ? convertToPseudoClasses({
          styles: getBackground(
            styles,
            styles.customSettings,
            style.palette,
            themeImages
          ),
          styleMapping: styles.styleMapping,
          isSelectedItem,
          pseudoState,
          key,
          currentElementKey
        })
        : convertToPseudoClasses({
          styles: getBackground(
            style.elements[styles.styleMapping],
            styles.customSettings,
            style.palette,
            themeImages
          ),
          styleMapping: styles.styleMapping,
          isSelectedItem,
          pseudoState,
          key,
          currentElementKey
        });
    }
  );

  /**
   * Gets the background style of the parent container. This is only to be used
   * for special cases where a widget has a flyout trigger or something similar
   * where a background needs to be applied, but the widget itself has no
   * background set.
   */
  getParentBackground() {
    const { themeImages, style } = this.props;
    return this._getParentBackground(style, themeImages);
  }

  _getParentBackground = defaultMemoize((style, themeImages) => {
    return getBackground(
      { background: style.background.parentBackground },
      {},
      style.palette,
      themeImages
    );
  });

  /**
   * Gets the background style for an element in a merged theme style object.
   * Either inherits from a mapped parent element or uses the custom
   * settings defined in the object.
   */
  getElementSpacing(key) {
    const { style } = this.props;
    return this._getElementSpacing(key, style);
  }

  _getElementSpacing = memoizeOn(
    key => key,
    (key, style) => {
      const styles = getJSONValue(style, key);
      if (!styles) {
        return {};
      }
      return styles.styleMapping === 'custom'
        ? getSpacing(styles, styles.customSettings)
        : getSpacing(
            style.elements[styles.styleMapping],
            styles.customSettings
          );
    }
  );

  /**
   * Gets the text/font style for an element in a merged theme style object.
   * Either inherits from a mapped parent element or uses the custom
   * settings defined in the object.
   */
  getElementText(key) {
    const { style, pseudoState, isSelectedItem, currentElementKey } = this.props;
    return this._getElementText(key, style, pseudoState, isSelectedItem, currentElementKey);
  }

  _getElementText = memoizeOn(
    key => key,
    (key, style, pseudoState, isSelectedItem, currentElementKey) => {
      const styles = getJSONValue(style, key, pseudoState, isSelectedItem, currentElementKey);
      if (!styles) {
        return {};
      }
      return styles.styleMapping === 'custom'
        ? convertToPseudoClasses({
          styles: getText(
            styles,
            styles.customSettings,
            style.palette,
            style.fontPalette,
            style.customFonts
          ),
          styleMapping: styles.styleMapping,
          isSelectedItem,
          pseudoState,
          key,
          currentElementKey
        })
        : convertToPseudoClasses({
          styles: getText(
            style.elements[styles.styleMapping],
            styles.customSettings,
            style.palette,
            style.fontPalette,
            style.customFonts
          ),
          styleMapping: styles.styleMapping,
          isSelectedItem,
          pseudoState,
          key,
          currentElementKey
        });
    }
  );
}
